import { useCallback, useMemo, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import {
  Chat,
  MessageContent,
  MessageRequest,
  useChatMutation,
  useChatsQuery,
  useMessageMutation,
  useMessagesQuery,
} from "../../api/chatApi.ts";
import CompleteChat from "../../components/chat/Chat.tsx";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useTeacherQuery } from "../../api/teacherApi.ts";

const Chats = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { chatId } = useParams();
  const userId = auth.user?.profile.sub;
  const [searchParams, setSearchParams] = useSearchParams();
  const newChatUserId = searchParams.get("newChatUserId");
  const { data: messages, refetch: refetchMessages } = useMessagesQuery(
    chatId,
    {
      refetchInterval: 5000,
      enabled: !chatId?.startsWith("placeholder-"),
    },
  );
  const { data: chats, refetch: refetchChats } = useChatsQuery();
  const { mutate: postMessage } = useMessageMutation();
  const { data: newChatUser } = useTeacherQuery(newChatUserId ?? undefined);
  const { mutate: createChat } = useChatMutation();

  const updateChatId = useCallback(
    (newChatId?: string) => {
      const currentSearchParams = new URLSearchParams(searchParams);
      if (newChatId) {
        navigate({
          pathname: `/mystudio/messages/${newChatId}`,
          search: currentSearchParams.toString(),
        });
      } else {
        navigate({
          pathname: "/mystudio/messages",
          search: currentSearchParams.toString(),
        });
      }
    },
    [navigate, searchParams],
  );

  const removeNewUserChatIdQueryParam = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("newChatUserId");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!newChatUserId || !chats) return;

    if (userId === newChatUserId) {
      updateChatId(undefined);
      removeNewUserChatIdQueryParam();
      return;
    }
    const existingChat = chats.find(
      (chat) => chat.participant.id === newChatUserId,
    );

    if (existingChat) {
      updateChatId(existingChat.chatId);
      removeNewUserChatIdQueryParam();
    } else if (newChatUser) {
      const placeholderChatId = `placeholder-${newChatUserId}`;
      updateChatId(placeholderChatId);
    }
  }, [
    chats,
    newChatUser,
    newChatUserId,
    updateChatId,
    removeNewUserChatIdQueryParam,
    userId,
  ]);

  // trigger refetch of chats when messages change to properly reload unread counts etc.
  useEffect(() => {
    refetchChats().catch(console.error);
  }, [messages, refetchChats]);

  const chatsWithNewChatPlaceholder = useMemo(() => {
    const isNewChatUserIdPresent =
      newChatUser &&
      !chats?.find((chat) => chat.participant.id === newChatUserId);

    if (isNewChatUserIdPresent) {
      const placeholderChatId = `placeholder-${newChatUserId}`;
      const placeholderChat: Chat = {
        chatId: placeholderChatId,
        participant: {
          id: newChatUser.id,
          name: `${newChatUser.firstName} ${newChatUser.lastName || ""}`.trim(),
        },
      };
      return [placeholderChat, ...(chats || [])];
    }
    return chats || [];
  }, [chats, newChatUser, newChatUserId]);

  const sendMessage = (chatId: string, content: MessageContent) => {
    const messageRequest: MessageRequest = {
      chatId: chatId,
      text: content.text,
    };
    postMessage(messageRequest, {
      onSettled: () => {
        refetchMessages().catch(console.error);
        refetchChats().catch(console.error);
      },
    });
  };

  const createChatAndSendMessage = (content: MessageContent) => {
    createChat(newChatUserId!, {
      onSuccess: (newChat) => {
        updateChatId(newChat.chatId);
        removeNewUserChatIdQueryParam();
        sendMessage(newChat.chatId, content);
      },
      onError: (error) => {
        console.error("Failed to create chat:", error);
      },
    });
  };

  const handleSendMessage = (content: MessageContent) => {
    if (!chatId) return;
    const isPlaceholderChat = chatId.startsWith("placeholder-");
    if (isPlaceholderChat && newChatUser) {
      createChatAndSendMessage(content);
    } else {
      sendMessage(chatId, content);
    }
  };

  const handleChatSelect = (chat: Chat) => {
    updateChatId(chat.chatId);
  };

  const removeChatId = () => {
    updateChatId(undefined);
  };

  return (
    <div className="-mx-4 -mt-4 -mb-6 h-[calc(100vh-8rem)] overflow-hidden sm:h-[calc(100vh-4rem)]">
      <CompleteChat
        chats={chatsWithNewChatPlaceholder}
        messages={messages || []}
        currentChatId={chatId}
        currentUserId={userId}
        onChatSelect={handleChatSelect}
        onSendMessage={handleSendMessage}
        onBackToList={removeChatId}
      />
    </div>
  );
};

export default Chats;
